// ----------------------------------------------------------------------------
// Spring Calculator: Shock variant parsing
// ----------------------------------------------------------------------------
//
// Extracts stroke and eye-to-eye from a shock variant's option text. Pure
// module with no DOM access.
//
// ----------------------------------------------------------------------------

const MM_PER_INCH = 25.4;

// Catches "210x55mm", "8.5 x 2.5\"", "230 X 65" and so on.
const DIMENSION_PAIR_PATTERN = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(mm|in|"|”)?/i;
const MM_VALUE_PATTERN = /(\d+(?:\.\d+)?)\s*mm/gi;

// Strokes outside this window are not coil-shock strokes, so a lone "mm" value
// outside it is ignored rather than mistaken for a stroke.
const MIN_STROKE_MM = 35;
const MAX_STROKE_MM = 80;

/**
 * Work out stroke and eye-to-eye (both mm) from the variant's title and option
 * values. Either value is null when nothing recognisable is found.
 *
 * @param {{ title?: string, options?: string[] }} variant
 * @returns {{ strokeMm: number|null, eyeToEyeMm: number|null }}
 */
export const parseShockDimensions = (variant) => {
    const text = [variant.title, ...(variant.options || [])].filter(Boolean).join(' ');
    const pair = text.match(DIMENSION_PAIR_PATTERN);

    if (pair) {
        let a = parseFloat(pair[1]);
        let b = parseFloat(pair[2]);
        const unit = (pair[3] || '').toLowerCase();
        const isInches = unit === 'in' || unit === '"' || unit === '”' || (!unit && Math.max(a, b) < 20);

        if (isInches) {
            a *= MM_PER_INCH;
            b *= MM_PER_INCH;
        }

        return { eyeToEyeMm: Math.max(a, b), strokeMm: Math.min(a, b) };
    }

    const mmValues = [...text.matchAll(MM_VALUE_PATTERN)].map((match) => parseFloat(match[1]));
    const strokeCandidate = mmValues.find((value) => value >= MIN_STROKE_MM && value <= MAX_STROKE_MM);

    return { eyeToEyeMm: null, strokeMm: strokeCandidate || null };
};

/**
 * Nearest value from a list of allowed strokes.
 */
export const nearestStroke = (strokeOptions, strokeMm) => strokeOptions.reduce((prev, curr) => (
    Math.abs(curr - strokeMm) < Math.abs(prev - strokeMm) ? curr : prev
));
